/**
 * AI Sales Agent — client helpers for recommendations (Phase 2).
 * Recommendations are generated server-side; the client only records human
 * decisions (see firestore.rules → recommendations) and creates tasks via
 * existing sanitized + logged patterns.
 */

import {
  doc, updateDoc, setDoc, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generateTaskId, generateLogId } from './documentIdUtils';
import { allocateTaskNumber } from './associationUtils';
import { logEntityChanges } from './logger';
import { sanitizeString } from './sanitization';

export const REC_TYPE_META = {
  reorder: { label: 'Reorder', icon: '🔁' },
  reactivate: { label: 'Re-engage', icon: '💤' },
  follow_up: { label: 'Follow-up', icon: '📞' },
  opp_next_step: { label: 'Opportunity', icon: '🎯' },
  contact_gap: { label: 'Contacts', icon: '👤' },
  cross_sell: { label: 'Cross-sell', icon: '🧩' },
  seasonal: { label: 'Seasonal', icon: '📅' }
};

export const PRIORITY_META = {
  high: { label: 'High', className: 'bg-red-100 text-red-800', rank: 0 },
  medium: { label: 'Medium', className: 'bg-yellow-100 text-yellow-800', rank: 1 },
  low: { label: 'Low', className: 'bg-gray-100 text-gray-700', rank: 2 }
};

export const HEALTH_BAND_META = {
  Healthy: 'bg-green-100 text-green-800',
  Watch: 'bg-yellow-100 text-yellow-800',
  'At Risk': 'bg-red-100 text-red-800',
  Dormant: 'bg-gray-200 text-gray-700',
  Prospect: 'bg-blue-100 text-blue-800'
};

export const DISMISS_REASONS = [
  { code: 'already_handled', label: 'Already handled / in progress' },
  { code: 'not_relevant', label: 'Not relevant for this customer' },
  { code: 'bad_timing', label: 'Timing is wrong' },
  { code: 'incorrect_data', label: 'Data is wrong or incomplete' },
  { code: 'customer_declined', label: 'Customer declined / not interested' },
  { code: 'other', label: 'Other' }
];

export const CONFIDENCE_LEVELS = [
  { value: 'strong', label: 'Strongly believe' },
  { value: 'somewhat', label: 'Somewhat unsure' }
];

export const SNOOZE_OPTIONS = [3, 7, 14, 30];

export const OPEN_STATUSES = ['proposed', 'snoozed'];

const EVIDENCE_ROUTES = {
  order: id => `/order/${encodeURIComponent(id)}`,
  opp: id => `/opportunity/${encodeURIComponent(id)}`,
  task: id => `/tasks/${encodeURIComponent(id)}`
};

export const recommendationEvidenceHref = ref =>
  ref?.refId && Object.hasOwn(EVIDENCE_ROUTES, ref.kind) ? EVIDENCE_ROUTES[ref.kind](ref.refId) : undefined;

export const sortRecommendations = (a, b) =>
  (PRIORITY_META[a.priority]?.rank ?? 9) - (PRIORITY_META[b.priority]?.rank ?? 9) ||
  (b.confidence || 0) - (a.confidence || 0);

/** Snoozed recs whose snooze has ended are shown as proposed until the next engine run flips them. */
export function effectiveStatus(rec, now = new Date()) {
  if (rec.status === 'snoozed' && rec.snoozeUntil?.toDate && rec.snoozeUntil.toDate() <= now) return 'proposed';
  return rec.status;
}

export async function recordDecisionBriefFeedback(brief, useful, user) {
  const userId = auth.currentUser?.uid;
  if (!userId || !user?.uid || typeof useful !== 'boolean') {
    throw new Error('A signed-in user and a useful/not-useful choice are required.');
  }
  const rec = brief.recommendation;
  const id = generateLogId('DecisionBrief', `${rec.id}_${userId}`, 'feedback');
  const feedback = {
    userId, actedAsUid: userId !== user.uid ? user.uid : null,
    recommendationId: rec.id, customerId: rec.customerId,
    briefVersion: sanitizeString(brief.version, 80),
    inputHash: sanitizeString(brief.inputHash, 128),
    useful, createdAt: serverTimestamp()
  };
  await setDoc(doc(db, collectionPath('aiDecisionBriefFeedback'), id), feedback);
  await logEntityChanges({
    oldObj: null, newObj: feedback, fields: ['useful', 'recommendationId', 'briefVersion'],
    entityType: 'DecisionBrief', entityId: rec.customerId, user, action: 'Created',
    extraDetails: `Usefulness feedback for ${rec.id}${feedback.actedAsUid ? ` (via View As by ${userId})` : ''}`
  });
}

export async function refreshCustomerRecommendations(customerId) {
  const fn = httpsCallable(getFunctions(), 'refreshCustomerRecommendations');
  const res = await fn({ customerId });
  return res.data;
}

const actorName = (user) =>
  (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user?.displayName || user?.email || 'Unknown');

async function recordDecision(rec, changes, user, action, extra = '') {
  // During View As, `user` is the viewed-as rep but the write is authenticated as the real signed-in user
  const authUid = auth.currentUser?.uid || user.uid;
  const viewedAs = authUid !== user.uid ? user.uid : null;
  await updateDoc(doc(db, collectionPath('recommendations'), rec.id), {
    ...changes,
    actedAt: serverTimestamp(),
    actedBy: authUid,
    actedByName: actorName(user),
    actedAsUid: viewedAs,
    updatedAt: serverTimestamp()
  });
  await logEntityChanges({
    oldObj: { status: rec.status, customerId: rec.customerId },
    newObj: { status: changes.status, customerId: rec.customerId },
    fields: ['status'],
    entityType: 'Recommendation',
    entityId: rec.id,
    user,
    action,
    extraDetails: `${rec.title}${extra ? ` — ${extra}` : ''}${viewedAs ? ` (via View As by ${authUid})` : ''}`
  });
}

export function dismissRecommendation(rec, { reasonCode, reason, confidence }, user) {
  const feedback = {
    reasonCode,
    reason: sanitizeString(reason || '', 500),
    confidence: confidence || null,
    userId: user.uid
  };
  return recordDecision(rec, { status: 'dismissed', feedback }, user, 'Dismissed', `reason: ${reasonCode}${feedback.reason ? ` (${feedback.reason})` : ''}`);
}

export function snoozeRecommendation(rec, days, user) {
  const until = Timestamp.fromDate(new Date(Date.now() + days * 24 * 60 * 60 * 1000));
  return recordDecision(rec, { status: 'snoozed', snoozeUntil: until }, user, 'Snoozed', `${days} days`);
}

export function completeRecommendation(rec, user) {
  return recordDecision(rec, { status: 'completed' }, user, 'Completed');
}

/** Initial values for TaskModal when accepting a create_task recommendation. */
export function buildTaskPrefill(rec, user) {
  const p = rec.suggestedAction?.payload || {};
  const due = new Date();
  due.setUTCHours(12, 0, 0, 0);
  due.setUTCDate(due.getUTCDate() + (Number.isFinite(p.dueInDays) ? p.dueInDays : 3));
  const opportunityId = p.opportunityId || '';
  return {
    title: p.title || rec.title,
    description: [p.description, rec.rationale ? `Why: ${rec.rationale}` : ''].filter(Boolean).join('\n\n'),
    dueDate: due,
    uid: user?.uid || '',
    status: 'open',
    priority: p.priority || 'Medium',
    customerId: rec.customerId,
    opportunityId,
    associations: {
      customerId: rec.customerId,
      customerName: rec.customerName || null,
      ...(opportunityId ? { opportunityId, opportunityNumberLabel: opportunityId } : {})
    }
  };
}

/**
 * Creates a task from TaskModal output and marks the recommendation accepted.
 * @returns {Promise<string>} task document ID (TSK-#)
 */
export async function createTaskFromRecommendation(rec, taskData, user) {
  const seq = await allocateTaskNumber();
  const taskDocId = generateTaskId(seq);
  const dueDate = taskData.dueDate instanceof Date ? Timestamp.fromDate(taskData.dueDate) : taskData.dueDate;
  const customerId = taskData.customerId || rec.customerId;
  const opportunityId = taskData.opportunityId || rec.suggestedAction?.payload?.opportunityId || null;

  const task = {
    ...taskData,
    title: sanitizeString(taskData.title || '', 200),
    description: sanitizeString(taskData.description || '', 5000),
    taskNumber: seq,
    taskNumberLabel: taskDocId,
    status: 'open',
    priority: taskData.priority || 'Medium',
    dueDate,
    customerId,
    opportunityId,
    associations: taskData.associations || {
      customerId,
      customerName: rec.customerName || null,
      ...(opportunityId ? { opportunityId } : {})
    },
    source: 'ai_recommendation',
    recommendationId: rec.id,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(doc(db, collectionPath('tasks'), taskDocId), task);
  await logEntityChanges({
    oldObj: null,
    newObj: { ...task, dueDate },
    fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
    entityType: 'Task',
    entityId: taskDocId,
    user,
    action: 'Created',
    extraDetails: `Created from recommendation: ${rec.title}`
  });
  await recordDecision(rec, { status: 'accepted', resultRef: { type: 'task', id: taskDocId } }, user, 'Accepted', `task ${taskDocId}`);
  return taskDocId;
}
